import React, { useState, useEffect, useContext } from 'react';
import { useSnackbar } from 'notistack';
import { AuthContext } from '../context/AuthContext';
import { 
    Box, CircularProgress, Typography, Paper, Button, 
    FormControl, InputLabel, Select, MenuItem, 
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow 
} from '@mui/material';
import { getKumesler, getPerformansListesi, getPerformansGrafigi } from '../services/api';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import PerformansPuanHesabi from '../components/PerformansPuanHesabi';
import './PerformansRaporlari.css';

const PerformansRaporlari = () => {
    const [kumesler, setKumesler] = useState([]);
    const [selectedKumes, setSelectedKumes] = useState('');
    const [performansData, setPerformansData] = useState([]);
    const [chartData, setChartData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchAttempted, setSearchAttempted] = useState(false);
    const { enqueueSnackbar } = useSnackbar();
    const { token } = useContext(AuthContext);

    useEffect(() => {
        const fetchKumesler = async () => {
            if (!token) return;
            try {
                const response = await getKumesler();
                setKumesler(response.data.data || response.data || []);
            } catch (err) {
                enqueueSnackbar('Kümes listesi alınamadı.', { variant: 'error' });
            }
        };
        fetchKumesler();
    }, [token, enqueueSnackbar]);

    const handleListele = async () => {
        if (!selectedKumes) {
            enqueueSnackbar('Lütfen bir kümes seçin.', { variant: 'warning' });
            return;
        }
        
        setLoading(true);
        setSearchAttempted(true);
        setPerformansData([]);
        setChartData([]);

        try {
            const [listeRes, grafikRes] = await Promise.all([
                getPerformansListesi(selectedKumes),
                getPerformansGrafigi(selectedKumes)
            ]);
            
            const performansListesi = Array.isArray(listeRes.data) ? listeRes.data : [];
            const grafikVerileri = Array.isArray(grafikRes.data) ? grafikRes.data : [];
            
            setPerformansData(performansListesi);
            setChartData(grafikVerileri);

            if (performansListesi.length === 0 && grafikVerileri.length === 0) {
                enqueueSnackbar('Seçilen kümes için performans verisi bulunamadı.', { variant: 'info' });
            }
        } catch (err) {
            const errorMessage = err.response?.data?.message || err.message;
            enqueueSnackbar(`Rapor verileri alınamadı: ${errorMessage}`, { variant: 'error' });
            setPerformansData([]);
            setChartData([]);
        } finally {
            setLoading(false);
        }
    };
    
    const formatDate = (dateInput) => {
        if (!dateInput) return '-';
        if (typeof dateInput === 'object' && dateInput !== null && 'year' in dateInput) {
            const { year, month, day } = dateInput;
            const date = new Date(year, month - 1, day);
            if (isNaN(date.getTime())) return '-'; 
            return date.toLocaleDateString('tr-TR');
        }
        if (typeof dateInput === 'string') {
            const date = new Date(dateInput);
            if (isNaN(date.getTime())) return dateInput;
            return date.toLocaleDateString('tr-TR');
        }
        return '-';
    };
    
    const formatNumber = (num, decimals = 0) => {
        const number = Number(num);
        if (isNaN(number)) return num || '-';
        return number.toLocaleString('tr-TR', { 
            minimumFractionDigits: decimals, 
            maximumFractionDigits: decimals 
        });
    };

    return (
        <Paper sx={{ p: 2, mt: 2, backgroundColor: '#fdfdfd' }} className="performans-container">
            <Typography variant="h5" gutterBottom>Performans Raporları</Typography>
            
            <Box className="filter-bar" sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <FormControl sx={{ minWidth: 240, mr: 2 }} size="small" color="warning">
                    <InputLabel>Kümes Seçiniz</InputLabel>
                    <Select 
                        value={selectedKumes} 
                        label="Kümes Seçiniz" 
                        onChange={(e) => setSelectedKumes(e.target.value)}
                    >
                        <MenuItem value=""><em>Seçiniz...</em></MenuItem>
                        {kumesler.map((kumes) => (
                            <MenuItem key={kumes.KUMES_ID} value={kumes.KUMES_ID}>
                                {kumes.KUMES_ADI}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                
                <Button 
                    variant="contained" 
                    onClick={handleListele} 
                    disabled={!selectedKumes || loading}
                    color="warning" 
                >
                    Listele
                </Button>
            </Box>

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                    <CircularProgress color="warning" />
                </Box>
            ) : (
                <>
                    {performansData.length > 0 && (
                        <TableContainer component={Paper} sx={{ mt: 2, mb: 4 }}>
                            <Table className="performans-table" size="small" stickyHeader>
                                <TableHead sx={{ '& .MuiTableCell-root': { backgroundColor: '#ef6c00', color: 'white', fontWeight: 'bold' } }}>
                                    <TableRow>
                                        <TableCell>Dönem No</TableCell>
                                        <TableCell>Başlangıç Tarihi</TableCell>
                                        <TableCell>Bitiş Tarihi</TableCell>
                                        <TableCell>Süre (Gün)</TableCell>
                                        <TableCell>Başlangıç Sayı</TableCell>
                                        <TableCell>Bitiş Sayı</TableCell>
                                        <TableCell>Ölüm Sayı</TableCell>
                                        <TableCell>Araçta Ölü Sayı</TableCell>
                                        <TableCell>Ölüm Oranı</TableCell>
                                        <TableCell>Toplam Yem (kg)</TableCell>
                                        <TableCell>Toplam Üretim (kg)</TableCell>
                                        <TableCell>Ortalama Ağırlık (kg)</TableCell>
                                        <TableCell>Yem Üretim Oranı</TableCell>
                                        <TableCell>Günlük Ağırlık Artışı (gr)</TableCell>
                                        <TableCell>Performans Puanı</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {performansData.map((veri, index) => (
                                        <TableRow hover key={veri.DONEM_NO || index}>
                                            <TableCell>{veri.DONEM_NO}</TableCell>
                                            <TableCell>{formatDate(veri.BASLANGIC_TARIH)}</TableCell>
                                            <TableCell>{formatDate(veri.BITIS_TARIH)}</TableCell>
                                            <TableCell>{formatNumber(veri.SURE_GUN)}</TableCell>
                                            <TableCell>{formatNumber(veri.BASLANGIC_SAYI)}</TableCell>
                                            <TableCell>{formatNumber(veri.BITIS_SAYI)}</TableCell>
                                            <TableCell>{formatNumber(veri.OLUM_SAYI)}</TableCell>
                                            <TableCell>{formatNumber(veri.ARACTA_OLU_SAYI)}</TableCell>
                                            <TableCell>{formatNumber(veri.OLUM_ORANI, 2)}%</TableCell>
                                            <TableCell>{formatNumber(veri.TOPLAM_YEM_KG)}</TableCell>
                                            <TableCell>{formatNumber(veri.TOPLAM_URETIM_KG)}</TableCell>
                                            <TableCell>{formatNumber(veri.ORTALAMA_AGIRLIK_KG, 3)}</TableCell>
                                            <TableCell>{formatNumber(veri.YEM_URETIM_ORAN, 2)}</TableCell>
                                            <TableCell>{formatNumber(veri.GUNLUK_AGIRLIK_GR)}</TableCell>
                                            <TableCell><b>{formatNumber(veri.PERFORMANS_PUAN)}</b></TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}

                    <PerformansPuanHesabi chartData={chartData} />

                    {chartData.length > 0 && (
                        <Box className="chart-section" sx={{ mb: 4 }}>
                            <Typography variant="h6" align="center" gutterBottom>
                                Dönemsel Performans Grafiği
                            </Typography>
                            <ResponsiveContainer width="100%" height={400}>
                                <LineChart data={chartData}>
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis dataKey="DONEM_NO" />
                                    <YAxis />
                                    <Tooltip />
                                    <Legend />
                                    <Line 
                                        type="monotone" 
                                        name="Kümes Performansı" 
                                        dataKey="KUMES_PERFORMANS" 
                                        stroke="darkorange" 
                                        strokeWidth={3}
                                        activeDot={{ r: 8 }} 
                                    />
                                    <Line 
                                        type="monotone" 
                                        name="Ortalama Performans" 
                                        dataKey="ORTALAMA_PERFORMANS" 
                                        stroke="#82ca9d" 
                                    />
                                </LineChart>
                            </ResponsiveContainer>
                            <Box 
                                component="img" 
                                src="https://portal.ekumes.com.tr/eKumesMusteri/images/performansFormul.jpeg" 
                                alt="Performans Formülü" 
                                sx={{ maxWidth: '100%', mt: 2, display: 'block', mx: 'auto' }}
                            />
                        </Box>
                    )}
                    
                    {searchAttempted && !loading && chartData.length === 0 && performansData.length === 0 && (
                        <Box sx={{ p: 4, textAlign: 'center' }}>
                            <Typography>
                                Seçilen kümes için gösterilecek performans verisi bulunamadı.
                            </Typography>
                        </Box>
                    )}
                </>
            )}
        </Paper>
    );
};

export default PerformansRaporlari;